// What a page swapped in by pjax-max needs from the rest of its document, on
// a Shopify storefront. pjax-max swaps the [data-router-view] and, with the
// merge from @theme/pjax-max-head, the `pjax-zone` blocks the layout draws in
// <head>. This module is that merge with what a storefront needs on top
// (createShopifyHead), plus what the router does with the view's own
// stylesheets and scripts (adoptViewStylesheets, runViewScripts).
//
// App embeds. Shopify renders head embeds after {{ content_for_header }} and
// body embeds just before </body>, outside any zone the layout can draw, and
// they differ by page: Kaching's product data, Klaviyo's viewed product,
// Triple Whale's page data, BOGOS's current product, Rivo's template. Head
// embeds become zones, named by the `BEGIN app block` comments Shopify wraps
// them in. Body embeds are never replaced, because apps render their UI into
// them (Rivo's launcher is most of its block): an embed new to the page is
// appended, and one already here only runs the scripts of the page's copy
// that haven't run in this document yet. (Estimated Delivery's carries the
// page's product; pjax-apps.js brings it up to date after each navigation.)
//
// Scripts run as they would on a page load: once. The merge copies scripts
// in inert, then an external script runs if its URL is new to the document
// (Shopify's preloads.js, Shop Pay and the like must not boot twice, and a
// bundle only the new page loads, like sticky-add-to-cart.js, must run), and
// an inline one runs if its source is new (per-page payloads like `__st` and
// window.FoxTheme change between pages; boot code doesn't). Copies left
// unrun stay in place for code that looks itself up by `script[src]`.
//
// Shopify's analytics tail (trekkie, web pixels, ShopifyAnalytics.meta) sits
// outside the zones too and is deliberately left alone: re-running it would
// change what gets counted.

import { createHead } from "@theme/pjax-max-head";

const WRAPPER = "[data-router-wrapper]";
const APP_BLOCK_START = /^BEGIN app block: (\S+)/;
const APP_BLOCK_END = "END app block";

/** External scripts that have run in this document, by absolute URL. */
const loaded = new Set();
/** Inline scripts outside the view that have run in this document, by their source. */
const ranOutside = new Set();
/** Inline scripts in a view that have run in this document, by their source. */
const ranInView = new Set();

/** Script types the browser runs, as opposed to data blocks (JSON, templates) it never does. */
export function isJavaScript(script) {
  const type = (script.getAttribute("type") || "").trim().toLowerCase();
  return !type || type === "module" || /javascript|ecmascript/.test(type);
}

/** A short fingerprint of a script's source (FNV-1a, with its length). */
export function fingerprint(text) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) hash = Math.imul(hash ^ text.charCodeAt(i), 0x01000193);
  return `${text.length}:${(hash >>> 0).toString(36)}`;
}

/**
 * The `head` option for Core: pjax-max's merge of the layout's zones, with app
 * embeds and the run-once script rules described at the top of this file.
 * Call it once, as the router starts.
 */
export function createShopifyHead() {
  const merge = createHead({ rerunScripts: false });

  for (const script of document.scripts) {
    if (!isJavaScript(script)) continue;
    if (script.src) loaded.add(script.src);
    else (script.closest(WRAPPER) ? ranInView : ranOutside).add(fingerprint(script.textContent));
  }
  zoneAppEmbeds(document);

  return (page) => {
    zoneAppEmbeds(page);
    // Apps add scripts of their own after load; their URLs count as run.
    for (const script of document.scripts) if (script.src && isJavaScript(script)) loaded.add(script.src);

    const scripts = new Set(document.scripts);
    const noscripts = new Set(document.getElementsByTagName("noscript"));

    merge(page);

    for (const noscript of [...document.getElementsByTagName("noscript")]) {
      if (!noscripts.has(noscript)) disarm(noscript);
    }
    for (const script of [...document.scripts]) {
      if (!scripts.has(script) && !script.closest(WRAPPER)) runOutside(script);
    }

    mergeBodyAppEmbeds(page);

    // Scripts outside any zone that this page loads and no page before it did:
    // app embed extensions a product page adds, like Affirm's.
    for (const script of page.head.querySelectorAll("script[src]")) {
      if (isJavaScript(script) && !loaded.has(script.src) && !script.closest("noscript")) {
        document.head.append(runnable(script));
        loaded.add(script.src);
      }
    }
  };
}

/**
 * Move the stylesheets (and preloads) of a view that has just gone in into
 * <head>, so they outlive it, or drop them where <head> already has them.
 */
export function adoptViewStylesheets(view) {
  for (const link of view.querySelectorAll('link[rel="stylesheet"][href], link[rel="preload"][href], link[rel="modulepreload"][href]')) {
    if (inHead(link)) link.remove();
    else document.head.append(link);
  }
}

/**
 * Copy the first page's view stylesheets into <head>, so a later view that
 * finds them there (and drops its own) isn't left without them once the
 * first view has gone.
 */
export function adoptFirstViewStylesheets(view) {
  for (const link of view?.querySelectorAll('link[rel="stylesheet"][href]') ?? []) {
    if (!inHead(link)) document.head.append(link.cloneNode());
  }
}

/**
 * Run the scripts of a view that has just gone in, in order, once the old
 * view has left (inline scripts look things up in the document: the grid
 * layout in main-collection, Locksmith's forms). Inline scripts run on every
 * visit, as they would on a page load; external ones once per document.
 * A repeat run's errors go to console.debug: code written to run once per
 * page load can throw when it runs again (a top-level `const`).
 */
export function runViewScripts(view) {
  for (const script of [...view.querySelectorAll("script")]) {
    if (!isJavaScript(script)) continue;

    if (script.src) {
      if (!loaded.has(script.src)) {
        loaded.add(script.src);
        script.replaceWith(runnable(script));
      }
      continue;
    }

    if (!script.textContent.trim()) continue;

    const print = fingerprint(script.textContent);
    const again = ranInView.has(print);
    ranInView.add(print);
    insert(() => script.replaceWith(runnable(script)), again);
  }
}

function runOutside(script) {
  if (!isJavaScript(script)) return;

  if (script.src) {
    if (!loaded.has(script.src)) {
      loaded.add(script.src);
      script.replaceWith(runnable(script));
    }
    return;
  }

  const print = fingerprint(script.textContent);
  if (!script.textContent.trim() || ranOutside.has(print)) return;
  ranOutside.add(print);
  insert(() => script.replaceWith(runnable(script)), false);
}

/**
 * Body embeds: append the ones new to the page; for the ones already here,
 * run what the page's copy has that hasn't run, and leave their DOM alone.
 */
function mergeBodyAppEmbeds(page) {
  for (const embed of page.body.children) {
    if (!embed.matches(".shopify-app-block[id]")) continue;

    const live = document.getElementById(embed.id);

    if (!live) {
      const copy = document.importNode(embed, true);
      document.body.append(copy);
      for (const noscript of copy.querySelectorAll("noscript")) disarm(noscript);
      for (const script of [...copy.querySelectorAll("script")]) runOutside(script);
      continue;
    }

    for (const script of embed.querySelectorAll("script")) {
      if (!isJavaScript(script)) continue;

      if (script.src) {
        if (!loaded.has(script.src)) {
          loaded.add(script.src);
          live.append(runnable(script));
        }
        continue;
      }

      const print = fingerprint(script.textContent);
      if (!script.textContent.trim() || ranOutside.has(print)) continue;
      ranOutside.add(print);
      // Run where the page would have, then take it out: the embed keeps the
      // DOM its app built, and doesn't grow by a script each navigation.
      const copy = runnable(script);
      insert(() => live.append(copy), false);
      copy.remove();
    }
  }
}

/**
 * Turn Shopify's `BEGIN app block` / `END app block` comment pairs at the top
 * level of <head> into pjax-zone markers, so the merge keeps each head embed
 * in step with the page by name.
 */
function zoneAppEmbeds(doc) {
  let name = null;

  for (const node of doc.head.childNodes) {
    if (node.nodeType !== Node.COMMENT_NODE) continue;

    const text = node.textContent.trim();

    if (name === null) {
      const handle = text.match(APP_BLOCK_START)?.[1];
      if (!handle) continue;
      name = `app-block:${handle}`;
      node.textContent = ` pjax-zone:${name} `;
    } else if (text === APP_BLOCK_END) {
      node.textContent = ` /pjax-zone:${name} `;
      name = null;
    }
  }
}

/**
 * A copy of a script that runs when it's inserted. An external one that
 * wasn't `async` keeps its order relative to the others like it, as its
 * `defer` would have on a page load.
 */
function runnable(script) {
  const copy = document.createElement("script");

  for (const { name, value } of script.attributes) copy.setAttribute(name, value);

  if (script.src) {
    if (!script.hasAttribute("async")) copy.async = false;
  } else {
    copy.textContent = script.textContent;
  }

  return copy;
}

/**
 * Insert a script so it runs. With `quiet`, an error it throws as it runs
 * is logged at console.debug instead of reported as uncaught.
 */
function insert(put, quiet) {
  if (!quiet) {
    put();
    return;
  }

  const swallow = (event) => {
    if (event.target !== window) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    console.debug("[pjax] a view script threw as it ran again:", event.error ?? event.message);
  };

  window.addEventListener("error", swallow, true);
  try {
    put();
  } finally {
    window.removeEventListener("error", swallow, true);
  }
}

/**
 * The parsed page has scripting off, so its <noscript>s hold elements; copied
 * into this document they'd load (Judge.me's no-JS stylesheet). Turn them back
 * into the text a page load gives them.
 */
function disarm(noscript) {
  if (noscript.firstElementChild) noscript.textContent = noscript.innerHTML;
}

function inHead(link) {
  for (const other of document.head.querySelectorAll("link[href]")) {
    if (other !== link && other.rel === link.rel && other.href === link.href) return true;
  }
  return false;
}
